/**
 * services/autoRefresh.ts · v351 60s 自动刷新 (工厂大屏 / operator dashboard)
 *
 * 2026 趋势: operator dashboard 60s tick + current-shift focus
 *
 * 公开 API:
 *   - registerAutoRefresh(pageId, refresh, intervalMs?) · 页面 mount 时调
 *   - unregisterAutoRefresh(pageId) · 页面 unmount 时调 (内部 hashchange 自动 cleanup)
 *
 * 设计:
 *   - 仅 dashboard/home/big screen 类型页面注册 (operator focus)
 *   - 使用 requestIdleCallback 防主线程过载
 *   - 切页 (hashchange) 自动清理 interval
 *   - TV mode 自动启用 30s 高频刷新
 */

const REGISTRY = new Map<string, { refresh: () => void; intervalMs: number; timer: number }>();
const DEFAULT_INTERVAL_MS = 60_000;  // 60s · operator dashboard 标准
const TV_INTERVAL_MS = 30_000;       // 30s · 工厂挂屏更紧

let refreshDot: HTMLDivElement | null = null;
let initialized = false;

function ensureRefreshDot(): HTMLDivElement {
  if (refreshDot && document.body.contains(refreshDot)) return refreshDot;
  refreshDot = document.createElement('div');
  refreshDot.className = 'tn-auto-refresh-dot';
  refreshDot.title = '自动刷新中 · Tự động làm mới';
  document.body.appendChild(refreshDot);
  return refreshDot;
}

function flashDot(): void {
  const dot = ensureRefreshDot();
  dot.classList.add('tn-refreshing');
  setTimeout(() => dot.classList.remove('tn-refreshing'), 800);
}

function safeRun(fn: () => void): void {
  try {
    fn();
    flashDot();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[autoRefresh] tick error:', err);
  }
}

/**
 * 注册自动刷新 · 页面 mount 时调
 */
export function registerAutoRefresh(
  pageId: string,
  refresh: () => void,
  intervalMs?: number,
): void {
  // 已注册同 pageId · 先清掉
  if (REGISTRY.has(pageId)) {
    unregisterAutoRefresh(pageId);
  }

  const isTv = document.body.dataset.tvMode === '1' || document.body.dataset.tvMode === 'true';
  const ms = intervalMs ?? (isTv ? TV_INTERVAL_MS : DEFAULT_INTERVAL_MS);

  const tick = (): void => {
    if (document.visibilityState !== 'visible') return; // 切走 tab 不刷
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(() => safeRun(refresh));
    } else {
      safeRun(refresh);
    }
  };

  const timer = window.setInterval(tick, ms);
  REGISTRY.set(pageId, { refresh, intervalMs: ms, timer });
  ensureRefreshDot();
}

export function unregisterAutoRefresh(pageId: string): void {
  const entry = REGISTRY.get(pageId);
  if (!entry) return;
  clearInterval(entry.timer);
  REGISTRY.delete(pageId);
  if (REGISTRY.size === 0 && refreshDot) {
    refreshDot.remove();
    refreshDot = null;
  }
}

export function unregisterAll(): void {
  for (const [id] of REGISTRY) unregisterAutoRefresh(id);
}

export function initAutoRefresh(): void {
  if (initialized) return;
  initialized = true;
  // hashchange 切页清掉所有 refresh (页面自行重新注册)
  window.addEventListener('hashchange', unregisterAll);
}
